import React, { useRef, useState, ChangeEvent, FormEvent, useEffect, useCallback } from 'react';
import { Upload, Typography, Button, Alert, Progress, Space, Modal, Dropdown, MenuProps } from 'antd';
import { SearchOutlined, ArrowRightOutlined, GithubOutlined, FileTextOutlined, ExclamationCircleOutlined, CheckCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { AlertCircle, Info, CheckCircle, Github, CreditCard, User, ShieldCheck } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import InfoTooltip from '../InfoTooltip.tsx';
import SignupModal from '../SignupModal.tsx';
import { uploadGithubRepo, uploadZipFile, scanUrl, unzipFileClientSide, runClientSideScanners } from '../../services/apiService.ts';
import { supabase } from '../../services/supabaseClient.ts';
import { cacheZipUploadForLater, cacheGithubUploadForLater } from '../../utils/uploadCache.ts';
import { setRedirectIntent } from '../../utils/redirectIntent.ts';
import { supabaseStatusStore } from '../../utils/supabaseStatus.ts';
import { ONE_TIME_SCAN_LIMIT_BYTES, LIMIT_ERROR_MESSAGES } from '../../constants/scanLimits.ts';
import { NoCreditsModal } from '../Overlay/NoCreditsModal.tsx';
import { isDemoScan, createDemoProject, clearDemoScan } from '../../services/demoService.ts';
import { useSub } from '../../contexts/SubscriptionContext.tsx';
import { useSession } from '../../hooks/useSession.ts';
import { logEvent } from '../../utils/analytics.ts';
import { FileUploader } from '../FileUploader.tsx';
import { GitHubUploader } from '../GitHubUploader.tsx';
import { UploadProgress } from '../UploadProgress.tsx';
import type { UploadState, ScanCreditsStatus } from '../../types/upload.ts';
import { formatFileSizeAuto } from '../../utils/formatFileSize.ts';
import { SHOW_DEMO_FIRST } from '../../constants/features.ts';
import { COPY } from '../../constants/copy.ts';
import PostHogService from '../../services/postHogService.ts';
import { EXCLUDED_SENSITIVE_FILES } from '../../../supabase/functions/_shared/excludedFolders.ts';

const { Title, Text, Paragraph } = Typography;

export type UploadWizardProps = {
  variant?: 'hero' | 'full';
};

type UploadMethod = 'url' | 'zip' | 'github' | 'folder';

interface UploadResult {
  success: boolean;
  scan_id: string;
  storage_path?: string;
  file_size?: number;
  repository?: string;
  branch?: string;
  status?: string;
  error?: string;
}

export const WizardContainer: React.FC<{ 
  children: React.ReactNode; 
  variant: 'hero' | 'full';
}> = ({ children, variant }) => {
  const baseClasses = variant === 'hero' 
    ? 'w-full' 
    : 'max-w-2xl mx-auto';
  
  const containerClasses = variant === 'hero'
    ? '' // No extra container for hero - styling handled by parent
    : 'bg-white rounded-2xl shadow p-8 relative';
  
  return (
    <div className={baseClasses}>
      <div className={containerClasses}>
        {children}
      </div>
    </div>
  );
};

const COACHMARK_KEY = 'upload_onboarding_seen_v1';

const coachmarkSteps = [
  {
    icon: <ShieldCheck className="w-10 h-10 text-blue-600 mb-4" />,
    title: 'Welcome to SafeDev!',
    description: "We help you make your apps secure. Here's how you get started:",
  },
  {
    icon: <Info className="h-8 w-8 text-blue-500 mb-2" />,
    title: 'How to Upload ZIP Files',
    description: 'To security check your app, you can Upload your code as a ZIP file. Sensitive files like .env, secrets, and private keys are automatically excluded for your privacy. Drag & drop or click "Choose ZIP File".',
  },
  {
    icon: <Github className="h-8 w-8 text-gray-700 mb-2" />,
    title: 'GitHub Repo Uploads',
    description: 'Scan public GitHub repositories by pasting the repo URL. For private repos, download as ZIP and upload instead.',
  },
  {
    icon: <CreditCard className="h-8 w-8 text-yellow-500 mb-2" />,
    title: 'What Are Credits?',
    description: 'Each scan uses 1 credit. Free users get 3 credits to start. Upgrade for more scans and AI-powered fixes. Check your credits at the top of the page.',
  },
  {
    icon: <User className="h-8 w-8 text-green-500 mb-2" />,
    title: 'Access Your Profile',
    description: 'Manage your account, credits, and subscription in your profile. Click your avatar or the menu at the top right.',
  },
  {
    icon: <Upload className="w-10 h-10 text-green-600 mb-4" />,
    title: 'ZIP Upload',
    description: 'To security check your app, you can Upload your code as a ZIP file. Sensitive files like .env, secrets, and private keys are automatically excluded for your privacy. Drag & drop or click "Choose ZIP File".',
  },
];

const UploadWizard: React.FC<UploadWizardProps> = ({ variant = 'full' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { session, loading: sessionLoading } = useSession();
  const { data: subscription, canScan, loading: creditsLoading, refresh: refreshCredits } = useSub();
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [githubUrl, setGithubUrl] = useState('');
  const [githubRepoSize, setGithubRepoSize] = useState<number | null>(null);
  const [status, setStatus] = useState<string>('');
  const [localError, setLocalError] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showNoCreditsModal, setShowNoCreditsModal] = useState(false);
  const [creditError, setCreditError] = useState<string | null>(null);
  const [lastUploadedFile, setLastUploadedFile] = useState<File | null>(null);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showUnauthOverlay, setShowUnauthOverlay] = useState(false);
  const [processingFile, setProcessingFile] = useState(false);
  const [showAuthOrDemoModal, setShowAuthOrDemoModal] = useState(false);
  const [showCoachmark, setShowCoachmark] = useState(false);
  const [coachmarkStep, setCoachmarkStep] = useState(0);
  const [showZipHelpModal, setShowZipHelpModal] = useState(false);

  // Upload state
  const [uploadState, setUploadState] = useState<UploadState>({
    isUploading: false,
    progress: 0,
    method: null,
    result: null,
    error: null
  });

  // Refs for cleanup
  const abortControllerRef = useRef<AbortController | null>(null);

  // Determine analytics source based on variant
  const analyticsSource = variant === 'hero' ? 'hero' : 'upload-page';

  // Allow upload if: no session (demo), or has session and can scan, or still loading credits
  const canUpload = !session || canScan || creditsLoading;

  // Add clear UX messaging for different states
  const getUploadStatusMessage = () => {
    if (!session) {
      return {
        canUpload: true,
        message: "Try demo scan first - upload your own code after signup",
        buttonText: "Start Demo Scan",
        warningLevel: "info"
      };
    }
    if (creditsLoading) {
      return {
        canUpload: true,
        message: "Checking your scan credits...",
        buttonText: "Loading...",
        warningLevel: "info"
      };
    }
    if (!canScan) {
      return {
        canUpload: false,
        message: "No scan credits remaining. Upgrade to Pro or purchase one-time scans.",
        buttonText: "No Scan Credits Available",
        warningLevel: "error"
      };
    }
    return {
      canUpload: true,
      message: "Ready to scan your code",
      buttonText: "Start Security Scan",
      warningLevel: "success"
    };
  };

  const uploadStatus = getUploadStatusMessage();

  console.log('[UploadWizard] Upload permission check:', {
    session: !!session,
    canScan,
    creditsLoading,
    canUpload: uploadStatus.canUpload,
    message: uploadStatus.message,
  });

  // Analytics: Track upload page views for unauthenticated users
  useEffect(() => {
    if (!creditsLoading && !session && variant === 'full') {
      console.log('[UploadWizard] Tracking upload page view for unauthenticated user');
      PostHogService.capture('upload_page_view_loggedout', {
        location: window.location.pathname,
        source: 'direct_upload_visit',
        demo_first_enabled: SHOW_DEMO_FIRST
      });
    }
  }, [creditsLoading, session, variant]);

  // Refresh credits when component mounts or when user returns to the page (only for full variant)
  useEffect(() => {
    if (variant !== 'full') return;

    const handleFocus = () => {
      console.log('[UploadWizard] Page focused - refreshing credits');
      refreshCredits();
    };

    // Refresh credits immediately when component mounts
    refreshCredits();

    // Add event listeners for when user returns to the page
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', handleFocus);
      window.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
          console.log('[UploadWizard] Page became visible - refreshing credits');
          refreshCredits();
        }
      });

      return () => {
        window.removeEventListener('focus', handleFocus);
        window.removeEventListener('visibilitychange', handleFocus);
      };
    }
  }, [refreshCredits, variant]);

  // Check for demo mode on component mount (only for full variant)
  useEffect(() => {
    if (variant !== 'full') return;

    const urlParams = new URLSearchParams(window.location.search);
    const isDemoFromUrl = urlParams.get('demo') === 'true';
    const demoSource = urlParams.get('source');
    const isDemoFromState = location.state?.fromDemo === true;
    
    // Only trigger demo mode if explicitly requested via URL param with demo source
    // or navigation state (for React Router navigation)
    const shouldTriggerDemo = (isDemoFromUrl && demoSource === 'demo-button') || isDemoFromState;
    
    console.log('[UploadWizard] Demo mode check:', {
      isDemoFromUrl,
      demoSource,
      isDemoFromState,
      shouldTriggerDemo,
      locationState: location.state,
      currentPath: location.pathname,
      searchParams: window.location.search
    });
    
    if (shouldTriggerDemo) {
      console.log('[UploadWizard] Demo mode detected, auto-triggering demo scan...');
      setIsDemoMode(true);
      handleDemoScan();
    } else {
      // Clear any leftover demo state if this is a regular upload
      console.log('[UploadWizard] Regular upload mode - clearing any leftover demo state');
      clearDemoScan();
      setIsDemoMode(false);
      
      // Clear any pre-loaded demo files from UI state
      setZipFile(null);
      setGithubUrl('');
      setStatus('');
      setError(null);
      setLocalError('');
      
      console.log('[UploadWizard] UI state cleared for fresh upload experience');
    }
  }, [location, variant]);

  // Handle upgrade success redirect (only for full variant)
  useEffect(() => {
    if (variant !== 'full') return;

    const urlParams = new URLSearchParams(window.location.search);
    const upgradeSuccess = urlParams.get('upgrade_success') === 'true';
    const sessionId = urlParams.get('session_id');
    
    if (upgradeSuccess && sessionId) {
      console.log('[UploadWizard] User returned from successful upgrade, refreshing credits...', {
        sessionId: sessionId.substring(0, 20) + '...',
        timestamp: new Date().toISOString()
      });
      
      // Show success message
      setStatus('✅ Payment successful! Your credits have been updated. You can now scan your codebase.');
      
      // Refresh credits to reflect the new purchase
      refreshCredits().then(() => {
        console.log('[UploadWizard] Credits refreshed after successful upgrade');
        
        // Clean up URL parameters to avoid confusion
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, '', cleanUrl);
        
        // Clear status after 5 seconds
        setTimeout(() => {
          setStatus('');
        }, 5000);
      }).catch((err) => {
        console.error('[UploadWizard] Failed to refresh credits after upgrade:', err);
      });
    }
  }, [location.search, refreshCredits, variant]);

  // Handle demo scan workflow
  const handleDemoScan = async () => {
    setShowAuthOrDemoModal(false);
    setProcessingFile(true);
    setStatus('Loading demo project...');
    try {
      const response = await fetch('/safevibe-test-codebase.zip');
      const blob = await response.blob();
      const demoFile = new File([blob], 'safevibe-demo.zip', { type: 'application/zip' });
      setZipFile(demoFile);
      setStatus('Demo project loaded. Running scan...');
      // Set demo mode in localStorage for downstream detection
      localStorage.setItem('demo_scan_active', 'true');
      // Navigate directly to demo results page
      navigate('/results/demo');
    } catch (err) {
      setError('Failed to load demo project. Please try again.');
    } finally {
      setProcessingFile(false);
    }
  };

  const [truncatedFiles, setTruncatedFiles] = useState<string[]>([]);
  const [cleanedZipSize, setCleanedZipSize] = useState<number | null>(null);
  const [originalZipSize, setOriginalZipSize] = useState<number | null>(null);
  const [allTruncatedFiles, setAllTruncatedFiles] = useState<string[]>([]);
  const [additionallyTruncatedFiles, setAdditionallyTruncatedFiles] = useState<string[]>([]);

  const handleFileUpload = async (file: File) => {
      setZipFile(file);
    setTruncatedFiles([]); // Reset truncation info
      setError(null);
    setStatus('');
  };

  const isValidGithubUrl = (url: string) =>
    /^https:\/\/github\.com\/[^/]+\/[^/]+(\.git)?$/.test(url);
  
  const isValidWebsiteUrl = (url: string) => {
    try {
      const parsed = new URL(url);
      return ['http:', 'https:'].includes(parsed.protocol) && 
             !parsed.hostname.includes('localhost') &&
             !parsed.hostname.includes('127.0.0.1') &&
             !parsed.hostname.startsWith('192.168.') &&
             !parsed.hostname.startsWith('10.') &&
             !parsed.hostname.startsWith('172.');
    } catch {
      return false;
    }
  };

  const checkScanPermission = async (): Promise<boolean> => {
    console.log('[UploadWizard] Checking scan permission...');
    
    if (!session) {
      console.log('[UploadWizard] No session - allowing demo/client-side scan');
      return true;
    }

    // For authenticated users, check credits
    if (!canScan) {
      console.log('[UploadWizard] No scan credits available');
      setCreditError('You have no scan credits remaining. Please upgrade to Pro or purchase one-time scans.');
      setShowNoCreditsModal(true);
      return false;
    }

    console.log('[UploadWizard] Scan permission granted');
    return true;
  };

  const handleGithubUpload = async (repoUrl: string) => {
    console.log('[UploadWizard] Starting GitHub upload:', repoUrl);
    try {
      setLoading(true);
      setError(null);
      setLocalError('');
      setStatus('🔄 Downloading GitHub repository...');

      // Track upload start
      PostHogService.capture('upload_started', { 
        source: analyticsSource,
        upload_type: 'github',
        repo_url: repoUrl
      });

      // Check scan permission
      const hasPermission = await checkScanPermission();
      if (!hasPermission) {
        setLoading(false);
        return;
      }

      if (!session) {
        // Unified: Show auth/demo modal for unlogged-in users
        setShowAuthOrDemoModal(true);
        setLoading(false);
        return;
      }

      console.log('[UploadWizard] Session found - uploading GitHub repo to server');
      // For authenticated users, upload to server
      const uploadResult = await uploadGithubRepo({ repoUrl });
      if (uploadResult.success && uploadResult.scan_id) {
        console.log('[UploadWizard] GitHub upload successful:', uploadResult.scan_id);
        setStatus('✅ Repository downloaded! Starting security scan...');
        // Refresh credits before navigating
        await refreshCredits();
        setTimeout(() => {
          navigate(`/results/${uploadResult.scan_id}`);
        }, 1000);
      } else {
        throw new Error(uploadResult.error || 'GitHub upload failed');
      }
    } catch (err: any) {
      console.error('[UploadWizard] GitHub upload failed:', err);
      setError(err.message || 'GitHub upload failed');
      setStatus('');
    } finally {
      setLoading(false);
    }
  };

  const handleUrlScan = async (url: string) => {
    console.log('[UploadWizard] Starting URL scan:', url);
    try {
      setLoading(true);
      setError(null);
      setLocalError('');
      setStatus('🌐 Analyzing website...');

      // Track upload start
      PostHogService.capture('upload_started', { 
        source: analyticsSource,
        upload_type: 'url',
        website_url: url
      });

      // Check scan permission
      const hasPermission = await checkScanPermission();
      if (!hasPermission) {
        setLoading(false);
        return;
      }

      if (!session) {
        // Unified: Show auth/demo modal for unlogged-in users
        setShowAuthOrDemoModal(true);
        setLoading(false);
        return;
      }

      console.log('[UploadWizard] Session found - scanning URL on server');
      // For authenticated users, scan URL on server
      const scanResult = await scanUrl({ 
        url,
        config: {
                  respectRobots: true,
        maxAssets: 50,
        timeout: 30000,
        includeSourceMaps: true
      }
    });
    
    if (scanResult && scanResult.success && scanResult.scan_id) {
      console.log('[UploadWizard] URL scan successful:', scanResult.scan_id);
      setStatus('✅ Website analyzed! Starting security scan...');
      // Refresh credits before navigating
      await refreshCredits();
      setTimeout(() => {
        navigate(`/results/${scanResult.scan_id}`);
      }, 1000);
    } else {
      throw new Error(scanResult?.error || 'URL scan failed');
    }
    } catch (err: any) {
      console.error('[UploadWizard] URL scan failed:', err);
      setError(err.message || 'URL scan failed');
      setStatus('');
    } finally {
      setLoading(false);
    }
  };

  const handleScan = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setStatus('');
    if (!zipFile && !isValidGithubUrl(githubUrl) && !isValidWebsiteUrl(websiteUrl)) {
      setError('Please enter a website URL to scan, select a ZIP file, or enter a valid GitHub repository URL.');
      return;
    }
    setLoading(true);
    setLocalError('');
    setCreditError(null);

    try {
      if (!session) {
        // Unified: Show auth/demo modal for unlogged-in users
        setShowAuthOrDemoModal(true);
        setLoading(false);
        return;
      }
      // ...existing upload logic for logged-in users...
      if (websiteUrl && isValidWebsiteUrl(websiteUrl)) {
        console.log('[UploadWizard] Processing website URL scan');
        await handleUrlScan(websiteUrl);
      } else if (zipFile) {
        console.log('[UploadWizard] Processing ZIP file upload');
        setStatus('📤 Uploading ZIP file...');
        // Actually upload the ZIP file
        const uploadResult = await uploadZipFile(zipFile);
        if (uploadResult.success && uploadResult.scan_id) {
          setStatus('✅ ZIP uploaded! Starting security scan...');
          // Refresh credits before navigating
          await refreshCredits();
          setTimeout(() => {
            navigate(`/results/${uploadResult.scan_id}`);
          }, 1000);
        } else {
          throw new Error(uploadResult.error || 'ZIP upload failed');
        }
      } else if (githubUrl && isValidGithubUrl(githubUrl)) {
        console.log('[UploadWizard] Processing GitHub URL upload');
        setStatus('📤 Downloading repository...');
        await handleGithubUpload(githubUrl);
      }
    } catch (err: any) {
      console.error('[UploadWizard] Scan submission failed:', err);
      setError(err.message || 'Failed to start scan');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async () => {
    console.log('[UploadWizard] Retrying upload...');
    setError(null);
    setLocalError('');
    
    if (websiteUrl && isValidWebsiteUrl(websiteUrl)) {
      await handleUrlScan(websiteUrl);
    } else if (zipFile) {
      await handleFileUpload(zipFile);
    } else if (githubUrl && isValidGithubUrl(githubUrl)) {
      await handleGithubUpload(githubUrl);
    }
  };

  const isNetworkOrCORSError = error?.includes('fetch') || error?.includes('CORS') || error?.includes('network');
  const retrying = false;

  // Show no credits modal for authenticated users without credits
  if (!loading && !canScan && session && showNoCreditsModal) {
    return (
      <Modal
        open={showNoCreditsModal}
        onCancel={() => setShowNoCreditsModal(false)}
        footer={[
          <Button key="close" onClick={() => setShowNoCreditsModal(false)}>
            Close
          </Button>,
          <Button key="upgrade" type="primary" onClick={() => navigate('/upgrade')}>
            Upgrade Now
          </Button>,
        ]}
        centered
      >
        <div className="text-center py-4">
          <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} />
          <Title level={3}>No Scan Credits Available</Title>
          <Paragraph>
            You've used all your one-time and Pro scans. Upgrade to Pro or purchase a single scan to continue.
          </Paragraph>
        </div>
      </Modal>
    );
  }

  // Intercept file upload for unlogged-in users
  const handleFileUploadClick = () => {
    if (!session) {
      setShowAuthOrDemoModal(true);
      return false; // Prevent file picker
    }
    return true;
  };

  useEffect(() => {
    if (!session) return;
    if (!localStorage.getItem(COACHMARK_KEY)) {
      setShowCoachmark(true);
    }
  }, [session]);

  const handleCoachmarkNext = () => {
    if (coachmarkStep < coachmarkSteps.length - 1) {
      setCoachmarkStep(coachmarkStep + 1);
    } else {
      setShowCoachmark(false);
      localStorage.setItem(COACHMARK_KEY, '1');
    }
  };
  const handleCoachmarkSkip = () => {
    setShowCoachmark(false);
    localStorage.setItem(COACHMARK_KEY, '1');
  };

  return (
    <>
      {/* User Flow Status Message */}
      {!isDemoMode && (
        <div className={`mb-6 p-4 rounded-xl border ${
          uploadStatus.warningLevel === 'error' 
            ? 'bg-red-50 border-red-200 text-red-800'
            : uploadStatus.warningLevel === 'info'
            ? 'bg-blue-50 border-blue-200 text-blue-800'  
            : 'bg-green-50 border-green-200 text-green-800'
        }`}>
          <div className="flex items-center gap-3">
            {uploadStatus.warningLevel === 'error' ? (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            ) : uploadStatus.warningLevel === 'info' ? (
              <Info className="w-5 h-5 flex-shrink-0" />
            ) : (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <div className="flex-1">
              <p className="font-medium">{uploadStatus.message}</p>
              {!session && (
                <p className="text-sm mt-1 opacity-80">
                  📝 Note: Demo scans use sample code. Sign up to scan your own files and get AI-powered fix suggestions.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Demo Mode Banner - only show for full variant */}
      {variant === 'full' && isDemoMode && (
        <div className="mb-6 p-4 bg-gradient-to-r from-purple-100 to-blue-100 border border-purple-200 rounded-lg">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🧪</span>
            <div>
              <h3 className="font-semibold text-purple-900">Demo Mode Active</h3>
              <p className="text-purple-700 text-sm">
                Running a security scan on a sample vulnerable project to show you how SafeVibe works.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Unauthenticated User Overlay - show only when user tries to upload */}
      {!session && showUnauthOverlay && (
        <div 
          data-cy="upload-overlay"
          className="absolute inset-0 bg-white/90 backdrop-blur-sm rounded-2xl z-10 flex flex-col items-center justify-center p-8"
        >
          <div className="text-center max-w-md">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              {variant === 'hero' ? 'Try SafeVibe in 10 seconds — no signup required' : COPY.UPLOAD_LOGGED_OUT_HEADLINE}
            </h2>
            <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg mb-6">
              <p className="text-blue-900 font-medium mb-2">
                {variant === 'hero' ? 'Create free account to scan your own code.' : COPY.UPLOAD_LOGGED_OUT_OVERLAY}
              </p>
              <p className="text-blue-700 text-sm">
                {variant === 'hero' ? 'Unlock uploads after quick signup.' : COPY.UPLOAD_LOGGED_OUT_SECONDARY}
              </p>
            </div>
            <button
              data-cy="demo-scan-button"
              onClick={() => {
                setShowUnauthOverlay(false);
                handleDemoScan();
              }}
              disabled={loading}
              className="bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-6 rounded-lg w-full mb-4 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Loading Demo...' : `${variant === 'hero' ? 'Run demo scan now' : COPY.UPLOAD_DEMO_BUTTON} →`}
            </button>
            <button
              data-cy="signup-button"
              onClick={() => {
                setShowUnauthOverlay(false);
                setShowSignupModal(true);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              Create Free Account
            </button>
          </div>
        </div>
      )}

      {showCoachmark && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center relative animate-fade-in">
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700"
              onClick={handleCoachmarkSkip}
              aria-label="Skip onboarding"
            >
              ×
            </button>
            {coachmarkSteps[coachmarkStep].icon}
            <h2 className="text-2xl font-bold mb-2 text-gray-900">{coachmarkSteps[coachmarkStep].title}</h2>
            <p className="text-gray-700 mb-6">{coachmarkSteps[coachmarkStep].description}</p>
            <div className="flex items-center justify-center gap-2 mb-4">
              {coachmarkSteps.map((_, idx) => (
                <span
                  key={idx}
                  className={`h-2 w-2 rounded-full ${coachmarkStep === idx ? 'bg-blue-500' : 'bg-gray-300'}`}
                />
              ))}
            </div>
            <button
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg transition"
              onClick={handleCoachmarkNext}
            >
              {coachmarkStep === coachmarkSteps.length - 1 ? 'Got it!' : 'Next'}
            </button>
            <button
              className="block mx-auto mt-4 text-xs text-gray-400 hover:text-gray-700 underline"
              onClick={handleCoachmarkSkip}
            >
              Don't show again
            </button>
          </div>
        </div>
      )}

      {/* Upload form */}
      <form onSubmit={handleScan} className="w-full">
        {/* Compact upload section for hero variant */}
        {variant === 'hero' ? (
          <div className="space-y-6">
            {/* Primary: Website URL Input */}
            <div className="border-2 border-blue-400 bg-blue-50 rounded-xl p-6 text-center transition-colors">
              <SearchOutlined className="mx-auto w-10 h-10 text-blue-600 mb-3" />
              <h3 className="text-lg font-semibold text-blue-900 mb-2">🌐 Scan Live Website</h3>
              <p className="text-blue-700 text-sm mb-4">Enter any deployed website URL to scan for security vulnerabilities</p>
              <div className="space-y-3">
                <input
                  type="url"
                  value={websiteUrl}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setWebsiteUrl(e.target.value);
                    if (e.target.value) {
                      setZipFile(null);
                      setGithubUrl('');
                    }
                  }}
                  placeholder="https://your-website.com"
                  className="w-full px-4 py-3 border border-blue-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all placeholder-blue-400 bg-white"
                  disabled={!!zipFile || !!githubUrl}
                />
                {websiteUrl && !isValidWebsiteUrl(websiteUrl) && (
                  <p className="text-red-500 text-sm">Please enter a valid HTTP/HTTPS URL (localhost not allowed)</p>
                )}
                {websiteUrl && isValidWebsiteUrl(websiteUrl) && (
                  <div className="text-green-700 text-sm p-2 bg-green-50 border border-green-200 rounded">
                    ✅ Ready to scan {new URL(websiteUrl).hostname}
                  </div>
                )}
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-3 bg-white text-slate-500 font-medium">or upload source code</span>
              </div>
            </div>

            {/* Secondary: ZIP Upload */}
            <div className="border border-slate-300 hover:border-slate-400 rounded-xl p-6 text-center transition-colors group">
              <Upload className="mx-auto w-8 h-8 text-slate-400 group-hover:text-slate-500 mb-3 transition-colors" />
              <div className="flex flex-col items-center gap-3">
                <div className="text-amber-700 text-xs mt-2 mb-2 text-center bg-amber-50 border border-amber-200 rounded p-2">
                  <b>🔒 Your secrets are safe:</b> Sensitive files like <code>.env</code>, private keys, and credentials are <b>never uploaded</b>. This protects your privacy.
                </div>
                <FileUploader
                  onFileSelect={async (file: File, truncatedFiles?: string[], cleanedSize?: number, originalSize?: number, additionallyTruncated?: string[]) => {
                    setProcessingFile(true);
                    setZipFile(file);
                    setWebsiteUrl('');
                    setGithubUrl('');
                    setTruncatedFiles(truncatedFiles || []);
                    setAllTruncatedFiles(truncatedFiles || []);
                    setAdditionallyTruncatedFiles(additionallyTruncated || []);
                    setError(null);
                    setStatus('');
                    if (typeof cleanedSize === 'number') setCleanedZipSize(cleanedSize);
                    if (typeof originalSize === 'number') setOriginalZipSize(originalSize);
                    console.log('[UploadWizard] FileUploader onFileSelect called with:', {
                      fileName: file.name,
                      fileSize: file.size,
                      cleanedSize,
                      originalSize,
                      truncatedCount: truncatedFiles?.length || 0,
                      additionallyTruncatedCount: additionallyTruncated?.length || 0
                    });
                    setProcessingFile(false);
                  }}
                  disabled={uploadState.isUploading || loading || !canUpload || processingFile || !!websiteUrl || !!githubUrl}
                  loading={processingFile}
                />
                <div className="text-slate-500 text-xs mt-2 mb-2 text-center">
                  Large folders like node_modules, .git, and build artifacts are automatically excluded from your upload to save space and speed up scanning.
                </div>
              </div>
              {zipFile && (
                <div className="text-green-700 text-sm mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-center gap-2">
                    <CheckCircleOutlined className="w-4 h-4" />
                    <span className="font-medium">{zipFile.name}</span>
                  </div>
                  <div className="text-xs text-green-600 mt-1">Ready to scan</div>
                </div>
              )}
            </div>

            {/* Tertiary: GitHub */}
            <div className="space-y-3">
              <input
                type="url"
                value={githubUrl}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setGithubUrl(e.target.value);
                  if (e.target.value) {
                    setZipFile(null);
                    setWebsiteUrl('');
                  }
                }}
                placeholder="GitHub repository URL (optional)"
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all placeholder-slate-400"
                disabled={!!zipFile || !!websiteUrl}
              />
              {githubUrl && !isValidGithubUrl(githubUrl) && (
                <p className="text-red-500 text-sm">Please enter a valid GitHub URL</p>
              )}
            </div>

            {/* Upload hint and truncation info */}
            <div className="mt-3 space-y-2">
              <p className="text-xs text-gray-400">
                Large folders like node_modules are automatically excluded.
              </p>
              
              {/* Size reduction info */}
              {originalZipSize && cleanedZipSize && (
                <div className="p-2 bg-green-50 border border-green-200 rounded text-xs">
                  <div className="font-medium text-green-800">Size Optimization Applied</div>
                  <div className="text-green-700">
                    Reduced from {(originalZipSize / (1024 * 1024)).toFixed(1)}MB to {(cleanedZipSize / (1024 * 1024)).toFixed(1)}MB
                    {zipFile && (
                      <span className="text-green-600"> (Final: {(zipFile.size / (1024 * 1024)).toFixed(1)}MB)</span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Truncated files info */}
              {allTruncatedFiles.length > 0 && (
                <div className="p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                  <div className="font-medium text-yellow-800">Files Removed for Size Optimization</div>
                  <div className="text-yellow-700 mt-1">
                    {allTruncatedFiles.length} file{allTruncatedFiles.length !== 1 ? 's' : ''} removed to fit under 50MB limit
                  </div>
                  {additionallyTruncatedFiles.length > 0 && (
                    <div className="text-yellow-600 mt-1">
                      Including {additionallyTruncatedFiles.length} large file{additionallyTruncatedFiles.length !== 1 ? 's' : ''} 
                      (images, binaries, etc.)
                    </div>
                  )}
                  <details className="mt-1">
                    <summary className="cursor-pointer text-yellow-600 hover:text-yellow-800">
                      View removed files ({allTruncatedFiles.length})
                    </summary>
                    <div className="mt-1 max-h-20 overflow-y-auto bg-yellow-100 p-1 rounded">
                      {allTruncatedFiles.slice(0, 20).map((file, i) => (
                        <div key={i} className="text-xs text-yellow-800 font-mono">{file}</div>
                      ))}
                      {allTruncatedFiles.length > 20 && (
                        <div className="text-xs text-yellow-600">...and {allTruncatedFiles.length - 20} more</div>
                      )}
                    </div>
                  </details>
                </div>
              )}
            </div>

            {/* Status and Error Messages */}
            {status && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <p className="text-blue-700 text-sm font-medium text-center">{status}</p>
              </div>
            )}
            
            {(localError || error) && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-red-700 text-sm font-medium text-center">{localError || error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={
                loading ||
                processingFile ||
                !canUpload ||
                !(
                  (websiteUrl && isValidWebsiteUrl(websiteUrl)) ||
                  zipFile ||
                  (githubUrl && isValidGithubUrl(githubUrl))
                )
              }
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-4 px-6 rounded-xl font-semibold transition-all duration-200 disabled:from-slate-400 disabled:to-slate-400 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:transform-none disabled:shadow-none"
            >
              {processingFile ? (
                <div className="flex items-center justify-center gap-2">
                  <LoadingOutlined className="w-4 h-4 animate-spin" />
                  <span>Processing ZIP file...</span>
                </div>
              ) : loading ? (
                <div className="flex items-center justify-center gap-2">
                  <LoadingOutlined className="w-4 h-4 animate-spin" />
                  <span>{status || 'Scanning...'}</span>
                </div>
              ) : canUpload ? (
                <div className="flex items-center justify-center gap-2">
                  <span>{session ? 'Start Security Scan' : 'Start Demo Scan'}</span>
                  <ArrowRightOutlined className="w-4 h-4" />
                </div>
              ) : (
                'No Scan Credits Available'
              )}
            </button>
          </div>
        ) : (
          // Full upload form for upload page
          <>
            {/* Primary: Website URL Input */}
            <div className="border-2 border-blue-400 bg-blue-50 rounded-lg p-6 mb-6">
              <p className="text-blue-900 mb-3 text-lg font-semibold flex items-center gap-2">
                <SearchOutlined className="w-5 h-5" />
                🌐 Scan Live Website
              </p>
              <p className="text-blue-700 text-sm mb-4">Enter any deployed website URL to scan for security vulnerabilities in production</p>
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="url"
                  value={websiteUrl}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setWebsiteUrl(e.target.value);
                    if (e.target.value) {
                      setZipFile(null);
                      setGithubUrl('');
                    }
                  }}
                  placeholder="https://your-website.com"
                  className="flex-1 px-4 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  disabled={!!zipFile || !!githubUrl}
                />
                <InfoTooltip text="Enter a deployed website URL to scan for security vulnerabilities" />
              </div>
              {websiteUrl && !isValidWebsiteUrl(websiteUrl) && (
                <p className="text-red-500 text-sm">
                  Please enter a valid HTTP/HTTPS URL (localhost not allowed for security)
                </p>
              )}
              {websiteUrl && isValidWebsiteUrl(websiteUrl) && (
                <div className="text-green-600 text-sm mt-2 p-2 bg-green-50 rounded border border-green-200">
                  <div className="flex items-center gap-2">
                    <CheckCircleOutlined className="w-4 h-4" />
                    <span className="font-medium">Ready to scan {new URL(websiteUrl).hostname}</span>
                  </div>
                  <div className="text-xs text-green-700 mt-1">
                    Will analyze source files, JavaScript, CSS, and security headers
                  </div>
                </div>
              )}
            </div>

            {/* Secondary: GitHub Repository */}
            <div className="border border-gray-300 rounded-lg p-6 mb-6">
              <p className="text-gray-700 mb-3 text-lg font-semibold flex items-center gap-2">
                <GithubOutlined className="w-5 h-5" />
                GitHub Repository (Source Code)
              </p>
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="url"
                  value={githubUrl}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setGithubUrl(e.target.value);
                    setGithubRepoSize(null);
                    if (e.target.value) {
                      setZipFile(null);
                      setWebsiteUrl('');
                    }
                  }}
                  placeholder="https://github.com/username/repo.git"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={!!zipFile || !!websiteUrl}
                />
                <InfoTooltip text="Enter a public GitHub repository URL to scan for security issues" />
              </div>
              <div className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-2">
                <strong>📋 Note:</strong> Repository must be public. For private repositories, download as ZIP and upload instead.
              </div>
              {githubUrl && !isValidGithubUrl(githubUrl) && (
                <p className="text-red-500 text-sm">
                  Please enter a valid GitHub repository URL (e.g., https://github.com/username/repo.git)
                </p>
              )}
              {githubUrl && isValidGithubUrl(githubUrl) && (
                <div className="text-green-600 text-sm mt-2 p-2 bg-green-50 rounded border border-green-200">
                  <div className="flex items-center gap-2">
                    <CheckCircleOutlined className="w-4 h-4" />
                    <span className="font-medium">Valid GitHub URL</span>
                  </div>
                  <div className="text-xs text-green-700 mt-1">
                    Ready to scan • {githubUrl}
                  </div>
                </div>
              )}
            </div>

            {/* Tertiary: ZIP Upload */}
            <div className="border border-gray-300 rounded-lg p-8 text-center mb-6 flex flex-col items-center">
              <Upload className="mx-auto w-12 h-12 text-gray-400 mb-4" />
              <p className="text-gray-700 mb-2 text-lg font-semibold flex items-center gap-2">
                📁 Upload Source Code (ZIP)
                <button
                  type="button"
                  className="ml-2 text-blue-600 hover:text-blue-800 underline text-sm font-medium focus:outline-none"
                  onClick={() => setShowZipHelpModal(true)}
                  aria-label="Never zipped a repo before? Here's how"
                >
                  ❓ Never zipped a repo before? Here's how →
                </button>
              </p>
              <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded p-2 mb-2 flex items-center gap-2">
                <Info className="w-4 h-4 text-blue-400" />
                <span className="font-medium">🔐 No secrets uploaded. We automatically strip <code>.env</code>, API keys, and <code>node_modules</code>.</span>
                </div>
              <div className="text-xs text-gray-500 mb-2">
                We auto-ignore <code>node_modules</code>, <code>.git</code>, and secrets to keep it fast & private.
              </div>
              <div className="flex flex-col items-center w-full max-w-md mx-auto mb-2 gap-2">
                <FileUploader
                  onFileSelect={async (file: File, truncatedFiles?: string[], cleanedSize?: number, originalSize?: number, additionallyTruncated?: string[]) => {
                    setProcessingFile(true);
                    setZipFile(file);
                    setWebsiteUrl('');
                    setGithubUrl('');
                    setTruncatedFiles(truncatedFiles || []);
                    setAllTruncatedFiles(truncatedFiles || []);
                    setAdditionallyTruncatedFiles(additionallyTruncated || []);
                    setError(null);
                    setStatus('');
                    if (typeof cleanedSize === 'number') setCleanedZipSize(cleanedSize);
                    if (typeof originalSize === 'number') setOriginalZipSize(originalSize);
                    console.log('[UploadWizard] FileUploader onFileSelect called with:', {
                      fileName: file.name,
                      fileSize: file.size,
                      cleanedSize,
                      originalSize,
                      truncatedCount: truncatedFiles?.length || 0,
                      additionallyTruncatedCount: additionallyTruncated?.length || 0
                    });
                    setProcessingFile(false);
                  }}
                  disabled={uploadState.isUploading || loading || !canUpload || processingFile || !!websiteUrl || !!githubUrl}
                  loading={processingFile}
                />
                <div className="text-slate-500 text-xs mt-2 mb-2 text-center">
                  Large folders like node_modules, .git, and build artifacts are automatically excluded from your upload to save space and speed up scanning.
                </div>
                  <InfoTooltip text="Upload your project as a ZIP file for security scanning" />
              </div>
              {zipFile && (
                <div className="text-green-600 text-sm mt-2 p-2 bg-green-50 rounded border border-green-200">
                  <div className="flex items-center gap-2">
                    <CheckCircleOutlined className="w-4 h-4" />
                    <span className="font-medium">{zipFile.name}</span>
                  </div>
                  <div className="text-xs text-green-700 mt-1">
                    Size: {formatFileSizeAuto(zipFile.size)} • Ready to scan
                  </div>
                </div>
              )}
            </div>

            {/* Upload hint and truncation info */}
            <div className="mt-3 space-y-2">
              <p className="text-xs text-gray-400">
                Large folders like node_modules are automatically excluded.
              </p>
              
              {/* Size reduction info */}
              {originalZipSize && cleanedZipSize && (
                <div className="p-2 bg-green-50 border border-green-200 rounded text-xs">
                  <div className="font-medium text-green-800">Size Optimization Applied</div>
                  <div className="text-green-700">
                    Reduced from {(originalZipSize / (1024 * 1024)).toFixed(1)}MB to {(cleanedZipSize / (1024 * 1024)).toFixed(1)}MB
                    {zipFile && (
                      <span className="text-green-600"> (Final: {(zipFile.size / (1024 * 1024)).toFixed(1)}MB)</span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Truncated files info */}
              {allTruncatedFiles.length > 0 && (
                <div className="p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                  <div className="font-medium text-yellow-800">Files Removed for Size Optimization</div>
                  <div className="text-yellow-700 mt-1">
                    {allTruncatedFiles.length} file{allTruncatedFiles.length !== 1 ? 's' : ''} removed to fit under 50MB limit
                  </div>
                  {additionallyTruncatedFiles.length > 0 && (
                    <div className="text-yellow-600 mt-1">
                      Including {additionallyTruncatedFiles.length} large file{additionallyTruncatedFiles.length !== 1 ? 's' : ''} 
                      (images, binaries, etc.)
                    </div>
                  )}
                  <details className="mt-1">
                    <summary className="cursor-pointer text-yellow-600 hover:text-yellow-800">
                      View removed files ({allTruncatedFiles.length})
                    </summary>
                    <div className="mt-1 max-h-20 overflow-y-auto bg-yellow-100 p-1 rounded">
                      {allTruncatedFiles.slice(0, 20).map((file, i) => (
                        <div key={i} className="text-xs text-yellow-800 font-mono">{file}</div>
                      ))}
                      {allTruncatedFiles.length > 20 && (
                        <div className="text-xs text-yellow-600">...and {allTruncatedFiles.length - 20} more</div>
                      )}
                    </div>
                  </details>
                </div>
              )}
            </div>

            {/* Status and Error Messages */}
            {status && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-blue-700 text-sm">{status}</p>
              </div>
            )}
            
            {(localError || error) && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{localError || error}</p>
                {isNetworkOrCORSError && (
                  <button
                    className="mt-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded transition"
                    onClick={handleRetry}
                    disabled={retrying}
                  >
                    {retrying ? 'Retrying...' : 'Retry Upload'}
                  </button>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={
                loading ||
                processingFile ||
                !canUpload ||
                !(
                  (websiteUrl && isValidWebsiteUrl(websiteUrl)) ||
                  zipFile ||
                  (githubUrl && isValidGithubUrl(githubUrl))
                )
              }
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-4 px-6 rounded-xl font-semibold transition-all duration-200 disabled:from-slate-400 disabled:to-slate-400 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:transform-none disabled:shadow-none"
            >
              {processingFile ? (
                <div className="flex items-center justify-center gap-2">
                  <LoadingOutlined className="w-4 h-4 animate-spin" />
                  <span>Processing ZIP file...</span>
                </div>
              ) : loading ? (
                <div className="flex items-center justify-center gap-2">
                  <LoadingOutlined className="w-4 h-4 animate-spin" />
                  <span>{status || 'Scanning...'}</span>
                </div>
              ) : canUpload ? (
                <div className="flex items-center justify-center gap-2">
                  <span>{session ? 'Start Security Scan' : 'Start Demo Scan'}</span>
                  <ArrowRightOutlined className="w-4 h-4" />
                </div>
              ) : (
                'No Scan Credits Available'
              )}
            </button>

            {/* Credit Status Warning - Only show for authenticated users without credits */}
            {!loading && !canScan && session && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="text-yellow-600">⚠️</span>
                  <div>
                    <h4 className="font-semibold text-yellow-800">No Scan Credits Available</h4>
                    <p className="text-yellow-700 text-sm mt-1">
                      You've used all your scan credits. Upgrade to Pro or purchase one-time scans to continue.
                    </p>
                    <button
                      data-cy="get-more-scans"
                      onClick={() => navigate('/upgrade')}
                      className="mt-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded text-sm font-medium transition"
                    >
                      Get More Scans
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </form>

      <SignupModal
        isOpen={showSignupModal}
        onClose={() => setShowSignupModal(false)}
        source="upload_wizard"
      />

      {/* Loading Spinner - Show different states */}
      {(processingFile || loading || uploadState.isUploading) && (
        <div className="flex flex-col items-center justify-center gap-2 mt-4 z-50">
          <div className="flex items-center gap-2">
            <LoadingOutlined className="w-5 h-5 animate-spin" />
            <span>
              {processingFile 
                ? "Processing file..." 
                : (loading || uploadState.isUploading) 
                  ? "Scanning..." 
                  : "Loading..."
              }
            </span>
          </div>
          {!processingFile && cleanedZipSize !== null && originalZipSize !== null && (
            <div className="text-xs text-gray-500 mt-1">
              ZIP size: {formatFileSizeAuto(cleanedZipSize)} (cleaned) / {formatFileSizeAuto(originalZipSize)} (original)
            </div>
          )}
        </div>
      )}

      <Modal
        open={showAuthOrDemoModal}
        onCancel={() => setShowAuthOrDemoModal(false)}
        footer={null}
        centered
      >
        <div className="text-center p-6">
          <h2 className="text-xl font-bold mb-2">Sign in or Try Demo</h2>
          <p className="mb-4 text-gray-600">Sign in to upload your own code, or try a live demo with sample code.</p>
          <div className="flex flex-col gap-3">
            <Button type="primary" size="large" onClick={() => navigate('/signup?redirect=/upload')}>Sign In / Sign Up</Button>
            <Button type="default" size="large" onClick={handleDemoScan}>Try Demo Scan</Button>
          </div>
        </div>
      </Modal>

      {/* Modal for repo zipping help (Ravi persona) */}
      <Modal
        open={showZipHelpModal}
        onCancel={() => setShowZipHelpModal(false)}
        footer={null}
        centered
      >
        <div className="text-center p-6">
          <h2 className="text-xl font-bold mb-2">How to ZIP Your Repo</h2>
          <p className="mb-4 text-gray-600">To upload your code, compress your project folder into a .zip file. Here's how:</p>
          <ul className="text-left text-gray-700 list-disc list-inside space-y-2 mb-4">
            <li>On Mac: Right-click your project folder and select <b>Compress</b>.</li>
            <li>On Windows: Right-click your folder, choose <b>Send to &gt; Compressed (zipped) folder</b>.</li>
            <li>On Linux: Run <code>zip -r myproject.zip myproject/</code> in the terminal.</li>
          </ul>
          <div className="text-xs text-gray-500">Your <b>node_modules</b>, <b>.git</b>, and secrets are auto-ignored for privacy and speed.</div>
        </div>
      </Modal>
    </>
  );
};

export default UploadWizard;
